import {
  Avatar,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { DATE_TIME_FORMAT } from 'constants/date';
import { STORAGE_KEY } from 'constants/global';
import { daysOfWeekOptions } from 'features/create-request/helpers';
import { StyledRequestOverview } from 'features/overview/styled';
import {
  AC_BASE_REQUEST_STATUS,
  mapBaseRequestColor,
  mapBaseRequestTitle,
} from 'models/create-request';
import { OVERVIEW_CHILDREN_ITEM, OVERVIEW_RESPONSE } from 'models/overview';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from 'routers/routes';
import { useAppDispatch } from 'store/hook';
import { getBaseRequestAction } from 'store/reducers/overview/actionTypes';
import { updateStepTrial } from 'store/reducers/trial-request';
import { theme } from 'theme';
import {
  convertDobToAge,
  formatDate,
  getLocalStorage,
  pluralString,
  removeLocalStorage,
} from 'utils';

interface RequestOverviewProps {
  requestData: OVERVIEW_RESPONSE;
}

export const RequestOverview = ({ requestData }: RequestOverviewProps) => {
  const redirect = useNavigate();
  const dispatch = useAppDispatch();
  const pets = Object.keys(requestData.pets).filter(
    (key) => requestData.pets[key as keyof typeof requestData.pets] === true,
  );

  const timingDates = requestData.days_of_week
    .map((apiDay) => daysOfWeekOptions.find((option) => option.id === apiDay))
    .filter(Boolean);

  const getAddress = () => {
    if (!requestData.address) return 'No address';
    const { street, unit_number, block_number, country, postal_code } =
      requestData.address;
    return `${unit_number}, ${block_number} ${street}, ${country} ${postal_code}`;
  };

  const handleRetryPayment = () => {
    removeLocalStorage(STORAGE_KEY.QR_CODE_SEARCH_FEE);
    dispatch(updateStepTrial(3));
    redirect(APP_ROUTES.CREATE_REQUEST.to);
  };

  useEffect(() => {
    dispatch(
      getBaseRequestAction({
        data: { accessToken: getLocalStorage(STORAGE_KEY.ACCESS_TOKEN) },
      }),
    );
  }, [dispatch]);

  return (
    <StyledRequestOverview spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent={{ xs: 'start', md: 'space-between' }}
        spacing={{ xs: 2, md: 0 }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1, md: 0.5 }}
        >
          <Typography variant="Web_Label_18">Expected Start Date:</Typography>
          <Typography variant="Web_Title_18" color="primary">
            {formatDate(requestData.start_datetime, {
              format: DATE_TIME_FORMAT.NORMAL_DATE,
            })}
          </Typography>
        </Stack>
        <Typography
          variant="Web_Label_18"
          color={mapBaseRequestColor[requestData.status]}
          textTransform={'capitalize'}
        >
          {mapBaseRequestTitle[requestData.status]}
        </Typography>
      </Stack>
      <Divider />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 4, md: 14 }}
      >
        <Stack spacing={{ xs: 0.5 }}>
          <Typography variant="Web_Label_16" color={theme.palette.grey3.dark}>
            End Date
          </Typography>
          <Typography variant="Small_Body_16">
            {formatDate(requestData.end_datetime, {
              format: DATE_TIME_FORMAT.NORMAL_DATE,
            })}
          </Typography>
        </Stack>
        <Stack spacing={{ xs: 0.5 }}>
          <Typography variant="Web_Label_16" color={theme.palette.grey3.dark}>
            Estimated No. of Hours per week
          </Typography>
          <Typography variant="Small_Body_16">
            {pluralString('hour', requestData.no_of_hours_per_week)}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        className="child_panel"
        direction={{ xs: 'column', md: 'row' }}
        spacing={4}
      >
        {requestData.children.map((child: OVERVIEW_CHILDREN_ITEM) => (
          <Stack direction="row" key={child.id} spacing={1} alignItems="center">
            <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.main' }}>
              {child.full_name.charAt(0)}
            </Avatar>
            <Stack spacing={0.25}>
              <Typography variant="Title_Menu_14">{child.full_name}</Typography>
              <Typography variant="Small_Body_12">
                {convertDobToAge(child.date_of_birth)}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Stack spacing={{ xs: 0.5 }}>
        <Typography variant="Web_Label_16" color={theme.palette.grey3.dark}>
          Address
        </Typography>
        <Typography variant="Small_Body_12">{getAddress()}</Typography>
      </Stack>
      <Stack spacing={{ xs: 0.5 }}>
        <Typography variant="Web_Label_16" color={theme.palette.grey3.dark}>
          Dates & Timings
        </Typography>
        <Grid>
          {timingDates.map((day, index) => (
            <Typography variant="Small_Body_12" key={day.id}>
              {day.fullName}
              {index + 1 < timingDates.length && ', '}
            </Typography>
          ))}
        </Grid>
        <Typography variant="Small_Body_12">
          {formatDate(requestData.start_datetime, {
            format: 'hh:mm A',
          })}
          &nbsp;to&nbsp;
          {formatDate(requestData.end_datetime, {
            format: 'hh:mm A',
          })}
        </Typography>
      </Stack>
      <Stack spacing={{ xs: 0.5 }}>
        <Typography variant="Web_Label_16" color={theme.palette.grey3.dark}>
          Other Requirements
        </Typography>
        <Typography variant="Small_Body_12">
          {requestData.special_requirements || 'None'}
        </Typography>
      </Stack>
      <Stack spacing={{ xs: 0.5 }}>
        <Typography variant="Web_Label_16" color={theme.palette.grey3.dark}>
          Pets
        </Typography>
        <Grid>
          {pets.length > 0 ? (
            pets.map((pet, index) => {
              return (
                <Typography variant="Small_Body_12" key={index}>
                  {pet.charAt(0).toUpperCase() + pet.slice(1)}
                  {index + 1 < pets.length && ', '}
                </Typography>
              );
            })
          ) : (
            <Typography variant="Small_Body_12">None</Typography>
          )}
        </Grid>
      </Stack>
      <Divider />
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Stack
          color={theme.palette.grey3.dark}
          direction={{ xs: 'column', md: 'row' }}
          spacing={0.5}
        >
          <Typography variant="Small_Body_16">Created on:</Typography>
          <Typography variant="Small_Body_16">
            {formatDate(requestData.createdAt, {
              format: DATE_TIME_FORMAT.NORMAL_DATE,
            })}
          </Typography>
        </Stack>
        {(requestData.status === AC_BASE_REQUEST_STATUS.PAYMENT_FAILED ||
          requestData.status === AC_BASE_REQUEST_STATUS.PENDING_PAYMENT) && (
          <Button variant="contained" onClick={handleRetryPayment}>
            Make Payment
          </Button>
        )}
      </Stack>
    </StyledRequestOverview>
  );
};
